import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/accounting` — the migration's dashboard.
 *
 * Until Fakturoid is switched off, the question that matters is "does
 * Hub already hold everything?". That answer is the reconciliation
 * run's verdict: any difference, or any document whose PDF was never
 * archived, and the account stays open.
 */

type Difference = {
  side: "issued" | "received";
  fakturoidId: string;
  number: string | null;
  kind: string;
  detail: string;
};

type ReconcileResult = {
  checked?: { issued: number; received: number };
  differences?: Difference[];
  ok?: boolean;
  logs?: string[];
};

type SyncJobRow = {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  result: ReconcileResult | null;
};

type CutoverCheck = {
  key: string;
  label: string;
  ok: boolean;
  blocking: boolean;
  detail: string;
};

type ReconciliationResponse = {
  lastRun: SyncJobRow | null;
  lastImport: SyncJobRow | null;
  counts: { issued: number; received: number; withoutPdf: number };
};

export const Route = createFileRoute("/_app/accounting/cutover")({
  loader: async () => {
    try {
      const [reconciliation, cutover] = await Promise.all([
        api.get<ReconciliationResponse>("/api/accounting/reconciliation"),
        api.get<{ checks: CutoverCheck[]; ready: boolean }>("/api/accounting/cutover"),
      ]);
      return { ...reconciliation, cutover };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: CutoverPage,
});

function verdict(t: TFunction, lastRun: SyncJobRow | null, withoutPdf: number) {
  if (!lastRun) return { tone: "unknown", label: t("accCutover.verdict.never") } as const;
  if (lastRun.status === "failed")
    return { tone: "failed", label: t("accCutover.verdict.failed") } as const;
  if (lastRun.status === "running" || lastRun.status === "pending")
    return { tone: "running", label: t("accCutover.verdict.running") } as const;

  const differences = lastRun.result?.differences?.length ?? 0;
  if (differences > 0)
    return {
      tone: "differences",
      label: t("accCutover.verdict.differences", { count: differences }),
    } as const;
  if (withoutPdf > 0)
    return {
      tone: "differences",
      label: t("accCutover.verdict.withoutPdf", { count: withoutPdf }),
    } as const;
  return { tone: "clear", label: t("accCutover.verdict.clear") } as const;
}

function CutoverPage() {
  const { lastRun, lastImport, counts, cutover } = Route.useLoaderData();
  const router = useRouter();
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"import" | "reconcile" | null>(null);

  const state = verdict(t, lastRun, counts.withoutPdf);
  const differences = lastRun?.result?.differences ?? [];

  async function run(kind: "import" | "reconcile") {
    setBusy(kind);
    try {
      await api.post(
        kind === "import" ? "/api/accounting/import/run" : "/api/accounting/reconciliation/run",
        {},
      );
      // The job is queued, not done — the page reloads to pick up the
      // `running` row, and again by hand once it finishes.
      await router.invalidate();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accCutover.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accCutover.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy !== null} onClick={() => run("import")}>
            {busy === "import" ? t("accCutover.queueing") : t("accCutover.importButton")}
          </Button>
          <Button disabled={busy !== null} onClick={() => run("reconcile")}>
            {busy === "reconcile" ? t("accCutover.queueing") : t("accCutover.checkNow")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t("accCutover.canSwitchOff")}</CardTitle>
          <Badge
            variant={
              state.tone === "clear"
                ? "success"
                : state.tone === "differences"
                  ? "warning"
                  : state.tone === "failed"
                    ? "destructive"
                    : "outline"
            }
          >
            {state.label}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Figure label={t("accCutover.issuedDocuments")} value={counts.issued} />
            <Figure label={t("accCutover.receivedDocuments")} value={counts.received} />
            <Figure label={t("accCutover.withoutArchivedPdf")} value={counts.withoutPdf} />
            <Figure
              label={t("accCutover.documentsChecked")}
              value={
                (lastRun?.result?.checked?.issued ?? 0) + (lastRun?.result?.checked?.received ?? 0)
              }
            />
          </div>
          <p className="text-muted-foreground">
            {t("accCutover.lastCheck")}{" "}
            {lastRun?.finishedAt ? (
              <LocalDate value={lastRun.finishedAt} withTime />
            ) : (
              t("accCutover.never")
            )}{" "}
            · {t("accCutover.lastImport")}{" "}
            {lastImport?.finishedAt ? (
              <LocalDate value={lastImport.finishedAt} withTime />
            ) : (
              t("accCutover.never")
            )}
          </p>
          {lastRun?.error && <p className="text-red-600">{lastRun.error}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("accCutover.beforeCancel")}</CardTitle>
            <p className="mt-1 text-muted-foreground text-sm">{t("accCutover.beforeCancelHint")}</p>
          </div>
          <Badge variant={cutover.ready ? "success" : "warning"}>
            {cutover.ready ? t("accCutover.readyToSwitchOff") : t("accCutover.notYet")}
          </Badge>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            {cutover.checks.map((check) => (
              <li key={check.key} className="flex items-start gap-2">
                <span className={check.ok ? "text-emerald-600" : "text-muted-foreground"}>
                  {check.ok ? "✓" : "○"}
                </span>
                <span>
                  {check.label}
                  {!check.blocking && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      {t("accCutover.optional")}
                    </Badge>
                  )}
                  <span className="block text-muted-foreground text-xs">{check.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("accCutover.differences")}</CardTitle>
        </CardHeader>
        <CardContent>
          {differences.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {lastRun ? t("accCutover.nothingDiffers") : t("accCutover.noCheckYet")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accCutover.side")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accCutover.document")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accCutover.kind")}</th>
                  <th className="py-1 font-medium">{t("accCutover.detail")}</th>
                </tr>
              </thead>
              <tbody>
                {differences.map((d) => (
                  <tr key={`${d.side}-${d.fakturoidId}-${d.kind}`} className="border-t">
                    <td className="py-1 pr-4">{t(`accCutover.sides.${d.side}`)}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{d.number ?? d.fakturoidId}</td>
                    <td className="py-1 pr-4">
                      <Badge variant="outline" className="text-[10px]">
                        {d.kind}
                      </Badge>
                    </td>
                    <td className="py-1 text-muted-foreground">{d.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="font-semibold text-2xl">{value}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  );
}
